import {JSONSchema7} from 'json-schema';
import {ECpmCObjectStoreEntryProxyTypeApiEnum} from '../../../gen/cpm-c-api-ts/src/enums/c-customer/cpmc-object-store-entry-proxy.api-enums';
import {CpmCTaskInputOutputMappingApiSchema} from '../../../gen/cpm-c-api-ts/src/schemas/c-customer/cpmc-task.api-schemas';
import {ObjectStoreEntriesTransientCache} from '../transient-cache/object-store-entries.transient-cache';
import {ObjectStoreEntryUtil} from './object-store-entry.util';
import {ReferenceExpressionUtils} from './reference-expression.utils';

export abstract class ObjectStoreBindingUtils {
    /**
     * Invalidate any stale Object Store bindings in the provided mappings.
     * A binding is considered stale if it cannot be resolved against the current cache
     * or if its type does not match the expected type.
     *
     * @param mappings - Array of input/output mappings to check.
     * @param cache - The transient cache containing Object Store entries.
     * @param expectedTypeByProperty - Expected Object Store entry type for each property.
     * @returns True if any mappings were changed, false otherwise.
     */
    public static invalidateStaleObjectStoreBindings(
        mappings: Record<string, CpmCTaskInputOutputMappingApiSchema>[],
        cache: ObjectStoreEntriesTransientCache,
        expectedTypeByProperty: Record<string, ECpmCObjectStoreEntryProxyTypeApiEnum>
    ): boolean {
        let changed = false;

        for (const mapping of mappings) {
            for (const propertyName of Object.keys(mapping)) {
                const referenceExpression = mapping[propertyName]?.reference_expression;
                if (!referenceExpression) continue;

                const expectedType = expectedTypeByProperty[propertyName];

                if (
                    !ObjectStoreBindingUtils.isResolvableObjectStoreReference(referenceExpression, cache, expectedType)
                ) {
                    delete mapping[propertyName];
                    changed = true;
                }
            }
        }

        return changed;
    }

    /**
     * Check if the given Object Store reference expression is resolvable against the current cache
     * and matches the expected type.
     *
     * @param referenceExpression - The reference expression to check.
     * @param cache - The transient cache containing Object Store entries.
     * @param expectedType - The expected Object Store entry type.
     * @returns True if the reference is resolvable and matches the expected type, false otherwise.
     */
    private static isResolvableObjectStoreReference(
        referenceExpression: string,
        cache: ObjectStoreEntriesTransientCache,
        expectedType: ECpmCObjectStoreEntryProxyTypeApiEnum
    ): boolean {
        const expressions = ReferenceExpressionUtils.parseExpressions(referenceExpression, true);
        if (expressions.length !== 1) return false;

        const expression = expressions[0];
        console.log('VLA | Parsed expression:', expression);

        const objectStoreEntry = cache.getDefinedOrInheritedEntry(expression.attributeName);
        if (!objectStoreEntry) return false;

        const actualLevel = cache.getEntryLevel(objectStoreEntry);
        if (!actualLevel) return false;

        if (expectedType && objectStoreEntry.vtype !== expectedType) {
            return false;
        }

        return true;
    }

    /**
     * Build a mapping of property names to their expected Object Store entry types based on the given JSON schema.
     *
     * @param schema - The JSON schema to extract property types from.
     * @returns A record mapping property names to their expected Object Store entry types.
     */
    public static buildExpectedTypeByProperty(
        schema?: JSONSchema7
    ): Record<string, ECpmCObjectStoreEntryProxyTypeApiEnum> {
        if (!schema?.properties) return {};

        const result: Record<string, ECpmCObjectStoreEntryProxyTypeApiEnum> = {};
        for (const [propertyName, propertySchema] of Object.entries(schema.properties)) {
            try {
                const resolved = ObjectStoreEntryUtil.resolveTypeFromPluginFunctionJsonSchemaProperty(
                    propertySchema as JSONSchema7,
                    schema
                );
                if (resolved?.vtype) {
                    result[propertyName] = resolved.vtype;
                }
            } catch {
                // Ignore errors and continue
            }
        }

        return result;
    }
}
